import './style.css';
import {AdminLayout} from "../../layouts/admin-layout";
import {useEffect, useRef, useState} from "react";
import {useUserStats} from "../../contexts/UserStatsContext";
import {
    getGeneratedAddresses,
    exportGeneratedAddresses,
    GeneratedAddress,
    GetGeneratedAddressesRequest,
    ExportFilter
} from "../../services/generatedAddressService";
import {isAxiosError} from "axios";
import {addressTypeConfigs} from "../../webConstants";

const numberFormatter = new Intl.NumberFormat("en-US");
const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatNumberValue = (value: number) => numberFormatter.format(value);
const formatCurrencyValue = (value: number) => currencyFormatter.format(value);

type SortField = "created_at" | "address_type" | "original_address" | "balance";
type SortOrder = "asc" | "desc";

export function Dashboard() {
    const {profile, loading, error} = useUserStats();

    const [addresses, setAddresses] = useState<GeneratedAddress[]>([]);
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [addressesError, setAddressesError] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState<SortField>("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const [selectedAddress, setSelectedAddress] = useState<GeneratedAddress | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [originalCopied, setOriginalCopied] = useState<boolean>(false);
    const [generatedCopied, setGeneratedCopied] = useState<boolean>(false);
    const originalCopyTimeoutRef = useRef<number | null>(null);
    const generatedCopyTimeoutRef = useRef<number | null>(null);

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportFilter, setExportFilter] = useState<ExportFilter>("all");
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const itemsPerPage = 20;

    const generatedAddresses = profile?.generated_addresses_count;
    const computersTotal = profile?.computers_total;
    const computersOnline = profile?.computers_online;
    const unspentBalance = profile?.generated_addresses_balance;

    const unresolvedValue = loading ? "…" : "—";

    const displayValue = (
        value: number | undefined,
        formatter: (value: number) => string = formatNumberValue
    ) => {
        if (value === undefined || value === null) {
            return unresolvedValue;
        }
        return formatter(value);
    };

    useEffect(() => {
        const fetchAddresses = async () => {
            setAddressesLoading(true);
            setAddressesError(null);
            try {
                const params: GetGeneratedAddressesRequest = {
                    take: itemsPerPage,
                    offset: (currentPage - 1) * itemsPerPage,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                };
                const response = await getGeneratedAddresses(params);
                setAddresses(response.items);
                setTotal(response.total);
            } catch (err: any) {
                setAddressesError(err?.response?.data?.message || err?.message || "Failed to load addresses");
            } finally {
                setAddressesLoading(false);
            }
        };

        fetchAddresses();
    }, [currentPage, sortBy, sortOrder, itemsPerPage]);

    useEffect(() => {
        if (!isModalOpen) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsModalOpen(false);

                setOriginalCopied(false);
                setGeneratedCopied(false);
                if (originalCopyTimeoutRef.current) {
                    window.clearTimeout(originalCopyTimeoutRef.current);
                    originalCopyTimeoutRef.current = null;
                }
                if (generatedCopyTimeoutRef.current) {
                    window.clearTimeout(generatedCopyTimeoutRef.current);
                    generatedCopyTimeoutRef.current = null;
                }

                setTimeout(() => {
                    setSelectedAddress(null);
                }, 300);
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    useEffect(() => {
        return () => {
            if (originalCopyTimeoutRef.current) {
                window.clearTimeout(originalCopyTimeoutRef.current);
            }
            if (generatedCopyTimeoutRef.current) {
                window.clearTimeout(generatedCopyTimeoutRef.current);
            }
        };
    }, []);

    const handleExport = async () => {
        setExporting(true);
        setExportError(null);
        try {
            await exportGeneratedAddresses(exportFilter);
            setExportModalOpen(false);
        } catch (err: any) {
            if (isAxiosError(err) && err.response?.status === 403) {
                const blob = err.response.data;
                const text = await blob.text();
                try {
                    const json = JSON.parse(text);
                    setExportError(json.error || "Export is disabled for secure accounts.");
                } catch {
                    setExportError("Export is disabled for secure accounts.");
                }
            } else {
                setExportError(err?.message || "Export failed");
            }
        } finally {
            setExporting(false);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
        setCurrentPage(1);
    };

    const getSortIconClass = (field: SortField) => {
        if (sortBy !== field) {
            return "material-symbols-outlined text-lg opacity-30 group-hover:opacity-100 transition-opacity";
        }
        return "material-symbols-outlined text-lg opacity-100 transition-opacity";
    };

    const getSortIcon = (field: SortField) => {
        if (sortBy !== field) {
            return "unfold_more";
        }
        return sortOrder === "asc" ? "keyboard_arrow_up" : "keyboard_arrow_down";
    };

    const totalPages = Math.ceil(total / itemsPerPage);
    const startItem = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, total);

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push("...");
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push("...");
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push("...");
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push("...");
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const getAddressTypeConfig = (addressType: number) => {
        return addressTypeConfigs.find(config => config.type === addressType);
    };

    const handleViewAddress = (address: GeneratedAddress) => {
        setSelectedAddress(address);
        requestAnimationFrame(() => {
            setIsModalOpen(true);
        });
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);

        setOriginalCopied(false);
        setGeneratedCopied(false);
        if (originalCopyTimeoutRef.current) {
            window.clearTimeout(originalCopyTimeoutRef.current);
            originalCopyTimeoutRef.current = null;
        }
        if (generatedCopyTimeoutRef.current) {
            window.clearTimeout(generatedCopyTimeoutRef.current);
            generatedCopyTimeoutRef.current = null;
        }

        setTimeout(() => {
            setSelectedAddress(null);
        }, 300);
    };

    const handleCopyOriginalAddress = async () => {
        if (!selectedAddress) {
            return;
        }

        try {
            await navigator.clipboard.writeText(selectedAddress.original_address);
            setOriginalCopied(true);
            if (originalCopyTimeoutRef.current) {
                window.clearTimeout(originalCopyTimeoutRef.current);
            }
            originalCopyTimeoutRef.current = window.setTimeout(() => {
                setOriginalCopied(false);
            }, 1500);
        } catch {
            // no-op
        }
    };

    const handleCopyGeneratedAddress = async () => {
        if (!selectedAddress) {
            return;
        }

        try {
            await navigator.clipboard.writeText(selectedAddress.generated_address);
            setGeneratedCopied(true);
            if (generatedCopyTimeoutRef.current) {
                window.clearTimeout(generatedCopyTimeoutRef.current);
            }
            generatedCopyTimeoutRef.current = window.setTimeout(() => {
                setGeneratedCopied(false);
            }, 1500);
        } catch {
            // no-op
        }
    };

    const formatDateShort = (isoString: string) => {
        const date = new Date(isoString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${month} ${day}, ${year} ${hours}:${minutes}:${seconds}`;
    };

    const formatDateForTable = (isoString: string) => {
        const date = new Date(isoString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return {
            date: `${month} ${day}, ${year}`,
            time: `${hours}:${minutes}:${seconds}`
        };
    };

    return (
        <AdminLayout>
            <div className="w-full max-w-7xl flex flex-col gap-8 animate-[fadeIn_0.5s_ease-out]">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-3xl font-bold tracking-tight text-white drop-shadow-sm">Dashboard
                            Overview</h2>
                        <p className="text-slate-400">Real-time network analysis and address tracking.</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div
                        className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300">
                        <div
                            className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total generated addresses</span>
                                <span className="material-symbols-outlined text-blue-300/80">key</span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-2xl font-bold text-white">{displayValue(generatedAddresses)}</span>
                            </div>
                        </div>
                    </div>
                    <div
                        className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300">
                        <div
                            className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total computers</span>
                                <span className="material-symbols-outlined text-purple-400/80">computer</span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-2xl font-bold text-white">{displayValue(computersTotal)}</span>
                            </div>
                        </div>
                    </div>
                    <div
                        className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300">
                        <div
                            className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Online computers</span>
                                <span className="material-symbols-outlined text-green-300/80">sensors</span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-2xl font-bold text-white">
                                    {profile && typeof computersTotal === "number"
                                        ? formatNumberValue(computersOnline ?? 0)
                                        : unresolvedValue}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300">
                        <div
                            className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Unspent balance</span>
                                <span
                                    className="material-symbols-outlined text-amber-400/80">account_balance_wallet</span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-2xl font-bold text-white">{displayValue(unspentBalance, formatCurrencyValue)}</span>
                            </div>
                        </div>
                    </div>
                </div>
                {(error || addressesError) && (
                    <div className="glass-panel rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
                        {error || addressesError}
                    </div>
                )}
                <div className="glass-panel rounded-3xl overflow-hidden flex flex-col border border-white/5">
                    <div
                        className="p-6 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                    <span
                                        className="material-symbols-outlined text-slate-400">format_list_bulleted</span>
                            Generated Addresses
                        </h3>
                        <button
                            onClick={() => { setExportError(null); setExportFilter("all"); setExportModalOpen(true); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium text-white transition-colors"
                        >
                            <span className="material-symbols-outlined text-lg">download</span>
                            Export Keys
                        </button>
                    </div>
                    <div className="p-4 border-b border-white/5">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-sm text-slate-500">
                                Showing <span className="text-white font-medium">{startItem}</span> to <span
                                className="text-white font-medium">{endItem}</span> of <span
                                className="text-white font-medium">{total}</span> results
                            </div>
                            {totalPages > 0 && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                        <span className="material-symbols-outlined !text-lg">chevron_left</span>
                                    </button>
                                    {getPageNumbers().map((page, index) => {
                                        if (page === "...") {
                                            return (
                                                <span key={`ellipsis-${index}`} className="px-3 py-1.5 text-slate-500">...</span>
                                            );
                                        }
                                        const pageNum = page as number;
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                    currentPage === pageNum
                                                        ? "bg-primary text-white shadow-lg shadow-primary/25"
                                                        : "bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                        <span className="material-symbols-outlined !text-lg">chevron_right</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                            <tr className="bg-black/20 text-xs uppercase font-semibold text-slate-400 border-b border-white/5">
                                <th className="p-0 whitespace-nowrap">
                                    <button onClick={() => handleSort("created_at")} className="flex items-center gap-1 w-full h-full px-6 py-4 cursor-pointer hover:bg-white/5 transition-colors group">
                                        <span>Date</span>
                                        <span className={getSortIconClass("created_at")}>{getSortIcon("created_at")}</span>
                                    </button>
                                </th>
                                <th className="p-0 whitespace-nowrap">
                                    <button onClick={() => handleSort("address_type")} className="flex items-center gap-1 w-full h-full px-6 py-4 cursor-pointer hover:bg-white/5 transition-colors group">
                                        <span>Address Type</span>
                                        <span className={getSortIconClass("address_type")}>{getSortIcon("address_type")}</span>
                                    </button>
                                </th>
                                <th className="p-0 whitespace-nowrap">
                                    <button onClick={() => handleSort("original_address")} className="flex items-center gap-1 w-full h-full px-6 py-4 cursor-pointer hover:bg-white/5 transition-colors group">
                                        <span>Address</span>
                                        <span className={getSortIconClass("original_address")}>{getSortIcon("original_address")}</span>
                                    </button>
                                </th>
                                <th className="p-0 whitespace-nowrap">
                                    <button onClick={() => handleSort("balance")} className="flex items-center gap-1 w-full h-full px-6 py-4 cursor-pointer hover:bg-white/5 transition-colors group">
                                        <span>Balance</span>
                                        <span className={getSortIconClass("balance")}>{getSortIcon("balance")}</span>
                                    </button>
                                </th>
                                <th className="px-6 py-4 whitespace-nowrap text-right"></th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 text-sm">
                            {addressesLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-slate-400">
                                        Loading...
                                    </td>
                                </tr>
                            ) : addresses.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-slate-400">
                                        No addresses found
                                    </td>
                                </tr>
                            ) : (
                                addresses.map((address) => {
                                    const addressTypeConfig = getAddressTypeConfig(address.address_type);
                                    const dateFormatted = formatDateForTable(address.created_at);
                                    const addressShort = address.generated_address.length > 10
                                        ? `${address.generated_address.slice(0, 6)}...${address.generated_address.slice(-4)}`
                                        : address.generated_address;

                                    return (
                                        <tr key={address.id} className="group hover:bg-white/[0.03] transition-colors">
                                            <td className="px-6 py-4 text-slate-300">
                                                {dateFormatted.date} <span className="text-slate-500 ml-1">{dateFormatted.time}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {addressTypeConfig ? (
                                                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                                                            {addressTypeConfig.icon}
                                                        </div>
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                                                            <span className="material-symbols-outlined text-lg text-slate-400">token</span>
                                                        </div>
                                                    )}
                                                    <span className="text-white font-medium">{addressTypeConfig?.label || `Type ${address.address_type}`}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 font-mono text-slate-400">{addressShort}</td>
                                            <td className={`px-6 py-4 font-medium ${address.balance > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {formatCurrencyValue(address.balance)}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => handleViewAddress(address)}
                                                    className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-white transition-colors border border-white/5 hover:border-white/10">
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 border-t border-white/5 flex items-center justify-between">
                        <div className="text-sm text-slate-500">
                            Showing <span className="text-white font-medium">{startItem}</span> to <span
                            className="text-white font-medium">{endItem}</span> of <span
                            className="text-white font-medium">{total}</span> results
                        </div>
                        {totalPages > 0 && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                {getPageNumbers().map((page, index) => {
                                    if (page === "...") {
                                        return (
                                            <span key={`ellipsis-${index}`} className="px-3 py-1.5 text-slate-500">...</span>
                                        );
                                    }
                                    const pageNum = page as number;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? "bg-primary text-white shadow-lg shadow-primary/25"
                                                    : "bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {selectedAddress && (
                <div
                    aria-modal="true"
                    className={`fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 transition-opacity duration-300 ease-in-out ${
                        isModalOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                    role="dialog"
                    onClick={handleCloseModal}
                >
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"></div>
                    <div
                        className={`relative w-full max-w-2xl transform rounded-2xl glass-panel shadow-2xl transition-all border border-white/10 flex flex-col ${
                            isModalOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-[0.96] opacity-0 translate-y-2'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                            <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
                                Address Details
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="rounded-lg p-2 text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-8 max-h-[80vh] overflow-y-auto">
                            {(() => {
                                const addressTypeConfig = getAddressTypeConfig(selectedAddress.address_type);
                                const dateFormatted = formatDateShort(selectedAddress.created_at);
                                return (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-1.5">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date Detected</p>
                                                <div className="flex items-center gap-2 text-slate-200 bg-white/5 px-3 py-2 rounded-lg border border-white/5">
                                                    <span className="material-symbols-outlined text-slate-400 text-lg">calendar_today</span>
                                                    <span>{dateFormatted}</span>
                                                </div>
                                            </div>
                                            <div className="space-y-1.5">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Address Type</p>
                                                <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg border border-white/5">
                                                    {addressTypeConfig ? (
                                                        <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center">
                                                            {addressTypeConfig.icon}
                                                        </div>
                                                    ) : (
                                                        <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center">
                                                            <span className="material-symbols-outlined text-sm text-slate-400">token</span>
                                                        </div>
                                                    )}
                                                    <span className="text-white font-medium">{addressTypeConfig?.label || `Type ${selectedAddress.address_type}`}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="space-y-1.5">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Original Address</p>
                                                    <button
                                                        onClick={handleCopyOriginalAddress}
                                                        title={originalCopied ? "Copied!" : "Copy"}
                                                        className={`text-[10px] font-medium uppercase tracking-wider flex items-center gap-1 transition-colors ${
                                                            originalCopied ? "text-emerald-400" : "text-primary hover:text-primary/80"
                                                        }`}
                                                    >
                                                        <span className="material-symbols-outlined text-sm">
                                                            {originalCopied ? "done" : "content_copy"}
                                                        </span>
                                                        {originalCopied ? "Copied" : "Copy"}
                                                    </button>
                                                </div>
                                                <div className="bg-black/30 rounded-lg p-3 border border-white/5 font-mono text-sm text-slate-300 break-all select-all">
                                                    {selectedAddress.original_address}
                                                </div>
                                            </div>
                                            <div className="space-y-1.5">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Generated Address</p>
                                                    <button
                                                        onClick={handleCopyGeneratedAddress}
                                                        title={generatedCopied ? "Copied!" : "Copy"}
                                                        className={`text-[10px] font-medium uppercase tracking-wider flex items-center gap-1 transition-colors ${
                                                            generatedCopied ? "text-emerald-400" : "text-primary hover:text-primary/80"
                                                        }`}
                                                    >
                                                        <span className="material-symbols-outlined text-sm">
                                                            {generatedCopied ? "done" : "content_copy"}
                                                        </span>
                                                        {generatedCopied ? "Copied" : "Copy"}
                                                    </button>
                                                </div>
                                                <div className="bg-black/30 rounded-lg p-3 border border-white/5 font-mono text-sm text-slate-300 break-all select-all">
                                                    {selectedAddress.generated_address}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-1.5">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Balance</p>
                                                <div className={`${selectedAddress.balance > 0 ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-rose-500/5 border-rose-500/10'} rounded-lg p-3 border flex items-center gap-3`}>
                                                    <div className={`p-1.5 ${selectedAddress.balance > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'} rounded-md`}>
                                                        <span className="material-symbols-outlined text-xl">account_balance_wallet</span>
                                                    </div>
                                                    <span className={`text-xl font-bold ${selectedAddress.balance > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                        {formatCurrencyValue(selectedAddress.balance)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="space-y-1.5">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">IP Address</p>
                                                <div className="bg-white/5 rounded-lg p-3 border border-white/5 flex items-center gap-3 text-slate-300">
                                                    <div className="p-1.5 bg-white/5 rounded-md text-slate-400">
                                                        <span className="material-symbols-outlined text-xl">dns</span>
                                                    </div>
                                                    <span className="font-mono text-sm" style={{overflowWrap: 'anywhere'}}>{selectedAddress.ip_address}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Checked</p>
                                            <div className="flex items-center gap-2 text-slate-200 bg-white/5 px-3 py-2 rounded-lg border border-white/5">
                                                <span className="material-symbols-outlined text-slate-400 text-lg">schedule</span>
                                                <span>{formatDateShort(selectedAddress.updated_at)}</span>
                                            </div>
                                        </div>
                                        <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4 space-y-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="material-symbols-outlined text-rose-400">lock</span>
                                                <h4 className="text-sm font-bold text-rose-400 uppercase tracking-wider">Private Keys (Sensitive)</h4>
                                            </div>
                                            <div className="space-y-1.5 group">
                                                <div className="flex justify-between items-center">
                                                    <p className="text-[10px] font-semibold text-rose-300/70 uppercase tracking-wider">Hex Private Key</p>
                                                </div>
                                                <code className="block w-full bg-black/20 rounded p-2.5 font-mono text-xs text-rose-200/60 break-all border border-rose-500/10 blur-sm transition-all duration-200 group-hover:blur-none">
                                                    {selectedAddress.hex_private_key}
                                                </code>
                                            </div>
                                            {selectedAddress.wif_private_key !== "None" && selectedAddress.wif_private_key && (
                                                <div className="space-y-1.5 group">
                                                    <div className="flex justify-between items-center">
                                                        <p className="text-[10px] font-semibold text-rose-300/70 uppercase tracking-wider">WIF Private Key</p>
                                                    </div>
                                                    <code className="block w-full bg-black/20 rounded p-2.5 font-mono text-xs text-rose-200/60 break-all border border-rose-500/10 blur-sm transition-all duration-200 group-hover:blur-none">
                                                        {selectedAddress.wif_private_key}
                                                    </code>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        <div className="px-6 py-4 border-t border-white/5 bg-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span className="material-symbols-outlined text-sm">memory</span>
                                <span>Machine HWID: <span className="font-mono text-slate-300">{selectedAddress.machine_guid}</span></span>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-primary hover:bg-blue-600 text-white text-sm font-semibold transition-all shadow-lg shadow-primary/20">
                                Close Details
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {exportModalOpen && (
                <div
                    className={`fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 transition-opacity duration-300 ease-in-out opacity-100 pointer-events-auto`}
                    role="dialog"
                    aria-modal="true"
                    onClick={() => { if (!exporting) setExportModalOpen(false); }}
                >
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm"></div>
                    <div
                        className="relative w-full max-w-md transform rounded-2xl glass-panel shadow-2xl border border-white/10 flex flex-col scale-100 opacity-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                            <h3 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
                                <span className="material-symbols-outlined text-slate-400">download</span>
                                Export Private Keys
                            </h3>
                            <button
                                onClick={() => setExportModalOpen(false)}
                                disabled={exporting}
                                className="rounded-lg p-2 text-slate-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <p className="text-sm text-slate-400">Select which addresses to include in the export file.</p>
                            <div className="space-y-2">
                                <label
                                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                                        exportFilter === "all"
                                            ? "border-primary/50 bg-primary/5"
                                            : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                                    }`}
                                    onClick={() => setExportFilter("all")}
                                >
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        exportFilter === "all" ? "border-primary" : "border-white/30"
                                    }`}>
                                        {exportFilter === "all" && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-white">All Addresses</p>
                                        <p className="text-xs text-slate-500">Export keys for all generated addresses</p>
                                    </div>
                                </label>
                                <label
                                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                                        exportFilter === "with_balance"
                                            ? "border-emerald-500/50 bg-emerald-500/5"
                                            : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                                    }`}
                                    onClick={() => setExportFilter("with_balance")}
                                >
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        exportFilter === "with_balance" ? "border-emerald-500" : "border-white/30"
                                    }`}>
                                        {exportFilter === "with_balance" && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-white">With Balance Only</p>
                                        <p className="text-xs text-slate-500">Export keys only for addresses with balance &gt; 0</p>
                                    </div>
                                </label>
                            </div>
                            {exportError && (
                                <div className="p-3 rounded-lg border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-rose-400 text-lg">error</span>
                                    {exportError}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-white/5 bg-white/5 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setExportModalOpen(false)}
                                disabled={exporting}
                                className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium text-white transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="px-5 py-2.5 rounded-lg bg-primary hover:bg-blue-600 text-white text-sm font-semibold transition-all shadow-lg shadow-primary/20 disabled:opacity-50 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-lg">{exporting ? "hourglass_empty" : "download"}</span>
                                {exporting ? "Exporting..." : "Download"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

